import { ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { TxRequest } from '../api/types';
import { useAuth } from '../auth/AuthContext';

export function PageHeader({ crumb, title, desc, action }: { crumb?: string; title: string; desc?: string; action?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 3 }}>
      <Box sx={{ flexGrow: 1 }}>
        {crumb && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            {crumb}
          </Typography>
        )}
        <Typography variant="h1">{title}</Typography>
        {desc && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {desc}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

const TX_COLOR: Record<TxRequest['status'], 'default' | 'info' | 'success' | 'error'> = {
  REQUESTED: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};
const TX_LABEL: Record<TxRequest['status'], string> = {
  REQUESTED: '요청',
  PROCESSING: '처리중',
  COMPLETED: '완료',
  FAILED: '실패',
};

export function TxStatusChip({ status }: { status: TxRequest['status'] }) {
  return <Chip size="small" color={TX_COLOR[status]} label={TX_LABEL[status]} sx={{ fontWeight: 700 }} />;
}

/** 연결된 체인의 표시 이름 — /health 의 chainId 로 판정(개발·검증 환경은 테스트넷, 운영은 프라이빗 체인) */
export function networkLabel(chainId?: string | null): string {
  if (!chainId) return '체인 확인 중';
  if (chainId === '12') return '메타디움 테스트넷';
  if (chainId === '11') return '메타디움 메인넷';
  return `프라이빗 체인 · ${chainId}`;
}

/**
 * 트랜잭션 해시 — 외부 익스플로러가 아니라 서비스 자체 조회 화면(/explorer)으로 연결한다.
 * 프라이빗 체인에는 공용 익스플로러가 없기 때문이다. 조회 화면 권한이 없는 사용자는 해시 복사만 제공한다.
 */
export function TxHashLink({ hash }: { hash: string | null }) {
  const { hasRole } = useAuth();
  const [copied, setCopied] = useState(false);
  if (!hash) return <span style={{ color: '#98a2b3' }}>—</span>;
  const short = `${hash.slice(0, 10)}…${hash.slice(-6)}`;
  if (hasRole('admin', 'issuer_st', 'issuer_sc')) {
    return (
      <RouterLink to={`/explorer?q=${hash}`} style={{ fontFamily: 'monospace', fontSize: 12 }} title={hash}>
        {short} ↗
      </RouterLink>
    );
  }
  const onCopy = async () => {
    if (await copyText(hash)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} component="span">
      <Tooltip title={hash} arrow placement="top">
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{short}</Typography>
      </Tooltip>
      <Tooltip title={copied ? '복사됨!' : '해시 복사'} arrow placement="top">
        <IconButton size="small" onClick={onCopy} aria-label="트랜잭션 해시 복사"
          sx={{ fontSize: 12, width: 22, height: 22, color: copied ? 'success.main' : 'text.secondary' }}>
          {copied ? '✓' : '⧉'}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export function shortAddr(a?: string | null): string {
  return a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '-';
}

/** 클립보드 복사 — https/localhost는 Clipboard API, 그 외(http)는 execCommand 폴백 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* 폴백으로 진행 */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** 지갑 주소 — 축약 표시 + 전체주소 툴팁 + 복사 버튼 */
export function AddressWithCopy({ address }: { address?: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!address) return <span style={{ color: '#98a2b3' }}>-</span>;
  const onCopy = async () => {
    if (await copyText(address)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip title={address} arrow placement="top">
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13, cursor: 'default' }}>
          {shortAddr(address)}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? '복사됨!' : '주소 복사'} arrow placement="top">
        <IconButton size="small" onClick={onCopy} aria-label="지갑 주소 복사"
          sx={{ fontSize: 13, width: 24, height: 24, color: copied ? 'success.main' : 'text.secondary' }}>
          {copied ? '✓' : '⧉'}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/** 18 decimals 토큰 금액 → 사람이 읽는 문자열 */
export function fmtUnits(v: string | undefined, decimals = 18): string {
  if (!v) return '0';
  if (decimals <= 0) return Number(v).toLocaleString(); // 정수값(단가·수량 등) — slice(0,-0) 버그 회피
  const s = v.padStart(decimals + 1, '0');
  const intPart = s.slice(0, -decimals).replace(/^0+(?=\d)/, '');
  return Number(intPart).toLocaleString();
}
